<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useQuasar } from 'quasar';

import ReorderableTable from '@/components/ReorderableTable.vue';
import CreatePageDialog from '@/components/dialogs/CreatePageDialog.vue';
import { getTranslation } from '@/composables/data-display-utils';
import { pageAfterDeletion } from '@/composables/pagination-utils';
import { Constants } from '@/constants';
import { confirmDelete } from '@/helpers/dialogs.helper';
import { canBeDeleted, canBeHomepage, getPageStatusLabel, isHomepage, pagePath } from '@/helpers/page.helper';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { getMaxLengthText } from '@/helpers/text.helper';
import type { ActionMenuItem, PageDto } from '@/services/types/api.types';
import { useCommunityStore, usePagesStore } from '@/stores';

const router = useRouter();
const { t } = useI18n();
const $q = useQuasar();

const pagesStore = usePagesStore();
pagesStore.fetchAll();
const { community } = storeToRefs(useCommunityStore());
const { isLoading, pagination, pageListItems } = storeToRefs(pagesStore);

const homepageId = computed(() => community.value?.options?.pages?.homepage_id ?? null);

const titleOf = (page: PageDto) => getTranslation(page, 'title', page.language_enabled);

// The homepage is served at `/` whatever its slug; a page with neither has no address.
const addressOf = (page: PageDto) => {
  const path = pagePath(page, homepageId.value);
  return path === null ? '—' : `/${path}`;
};

const columns = computed(() => [
  {
    name: 'title',
    label: t('page.title'),
    field: (row: PageDto) => getMaxLengthText(titleOf(row)),
    align: 'left',
  },
  {
    name: 'address',
    label: t('page.slug-column'),
    field: (row: PageDto) => addressOf(row),
    align: 'left',
  },
  {
    name: 'status',
    label: t('page.status.label'),
    field: (row: PageDto) => getPageStatusLabel(row.status),
    align: 'left',
  },
]);

// ReorderableTable's own Delete cannot be disabled on a row, so the list brings its own, as ProjectsListView does.
const actionMenuItems = computed<ActionMenuItem[]>(() => [
  {
    action: 'set-homepage',
    label: t('page.actions.set-homepage'),
    disabled: (row: PageDto) => !canBeHomepage(row, homepageId.value).allowed,
    tooltip: (row: PageDto) => canBeHomepage(row, homepageId.value).reason,
  },
  {
    action: 'delete-row',
    label: t('global.label.delete'),
    disabled: (row: PageDto) => !canBeDeleted(row, homepageId.value).allowed,
    tooltip: (row: PageDto) => canBeDeleted(row, homepageId.value).reason,
  },
]);

function defaultLanguageEnabled() {
  return community.value?.language_enabled ?? [community.value?.default_language ?? Constants.DefaultLocale];
}

const handleRowClick = (row: PageDto) => {
  router.push({ name: 'page_details', params: { id: row.id } });
};

const handleAddRow = () => {
  $q.dialog({
    component: CreatePageDialog,
    componentProps: { languageEnabled: defaultLanguageEnabled() },
  }).onOk((page: PageDto) => {
    router.push({ name: 'page_details', params: { id: page.id } });
  });
};

const handleDeleteRow = (row: PageDto) => {
  confirmDelete(t('page.name')).onOk(() => {
    const page = pageAfterDeletion(pagination.value);
    wrapSavingOperation(async () => await pagesStore.delete(row.id, page));
  });
};

// Changing what `/` serves reaches every visitor at once, so it asks first, naming both pages.
const handleSetHomepage = async (row: PageDto) => {
  const current = await pagesStore.findHomepage();
  const message = current
    ? t('page.set-homepage.message', { new: titleOf(row), current: titleOf(current) })
    : t('page.set-homepage.message-first', { new: titleOf(row) });
  $q.dialog({
    title: t('page.set-homepage.title'),
    message,
    ok: { label: t('page.actions.set-homepage'), color: 'primary', unelevated: true },
    cancel: { label: t('global.cancel'), color: 'primary', flat: true },
  }).onOk(() => {
    wrapSavingOperation(async () => await pagesStore.setHomepage(row.id));
  });
};

const handleActions = (event: { action: string; payload: { row: PageDto } }) => {
  if (event.action === 'set-homepage') handleSetHomepage(event.payload.row);
};

const fetchPages = async (request: { page: number; rowsPerPage: number }) => {
  await pagesStore.fetchAll(request.page, request.rowsPerPage);
};
</script>

<template>
  <q-page class="pages-list">
    <h1 class="title">{{ t('page.pages') }}</h1>
    <reorderable-table
      :rows="pageListItems"
      :columns="columns"
      :loading="isLoading"
      item-key="id"
      :disable-delete-button="true"
      :disable-orderable="true"
      :pagination-enabled="true"
      :pagination="pagination"
      :action-menu-items="actionMenuItems"
      @row-click="handleRowClick"
      @add-row="handleAddRow"
      @delete-row="handleDeleteRow"
      @request="fetchPages"
      @actions="handleActions"
    >
      <template #body-cell-title="props">
        <q-td :props="props">
          {{ props.value }}
          <q-badge
            v-if="isHomepage(props.row, homepageId)"
            class="q-ml-sm"
            color="primary"
            :label="t('page.homepage')"
          />
        </q-td>
      </template>
    </reorderable-table>
  </q-page>
</template>
<style lang="scss">
.pages-list {
  padding: 16px;

  .title {
    margin: 0 10px !important;
  }
}
</style>
